/*
 * Discount code field for the cart (cart page + drawer).
 *
 * Loaded as an external, deferred script from the theme layouts (like theme.js) so the
 * custom element stays defined across the theme's instant navigation and section
 * re-renders — an inline <script> inside the section would only run on a full page load,
 * leaving re-rendered <cart-discount> elements un-upgraded and unresponsive.
 *
 * Apply / remove both use the AJAX Cart API:
 *   POST {cart_url}/update.js  body: { discount: "CODE", sections: ["mini-cart"] }
 * An empty discount ("") clears the applied code(s). The response holds the updated cart
 * JSON (discount_codes: [{ code, applicable }]) plus the freshly rendered mini-cart
 * section, which we hand to the theme's `cart:refresh` event so the drawer updates in
 * place. On the cart page we reload so the page totals update.
 */
(function () {
  if (customElements.get('cart-discount')) return;

  customElements.define('cart-discount', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-discount-input]');
      this.button = this.querySelector('[data-discount-apply]');
      this.message = this.querySelector('[data-discount-message]');

      if (this.button) {
        this.button.addEventListener('click', () => this.applyDiscount());
      }

      if (this.input) {
        this.input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.applyDiscount();
          }
        });
      }

      this.querySelectorAll('[data-discount-remove]').forEach((button) => {
        button.addEventListener('click', () => this.removeDiscount());
      });
    }

    get cartUrl() {
      return this.getAttribute('data-cart-url') || '/cart';
    }

    get isInDrawer() {
      return this.closest('.drawer') !== null;
    }

    setBusy(state) {
      if (state) {
        this.setAttribute('aria-busy', 'true');
      } else {
        this.removeAttribute('aria-busy');
      }
    }

    async updateDiscount(value) {
      const response = await fetch(this.cartUrl + '/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ discount: value, sections: ['mini-cart'] })
      });
      return response.json();
    }

    isApplied(cart, code) {
      const wanted = code.trim().toUpperCase();
      return (cart.discount_codes || []).some((entry) => {
        return entry.applicable === true && (entry.code || '').toUpperCase() === wanted;
      });
    }

    async applyDiscount() {
      if (!this.input) return;

      const code = this.input.value.trim();
      if (!code) {
        this.input.focus();
        return;
      }

      this.hideMessage();
      this.setBusy(true);

      try {
        const cart = await this.updateDiscount(code);

        if (this.isApplied(cart, code)) {
          this.input.value = '';
          this.refreshCart(cart);
          // Success: the cart re-renders (drawer) or reloads (cart page), which replaces
          // this element — no further state handling needed here.
        } else {
          this.showMessage(this.getAttribute('data-invalid-text') || '', 'error');
          this.setBusy(false);
        }
      } catch (error) {
        this.showMessage(this.getAttribute('data-invalid-text') || '', 'error');
        this.setBusy(false);
      }
    }

    async removeDiscount() {
      this.hideMessage();
      this.setBusy(true);

      try {
        const cart = await this.updateDiscount('');
        this.refreshCart(cart);
      } catch (error) {
        window.location.reload();
      }
    }

    refreshCart(cart) {
      if (!this.isInDrawer) {
        // Cart page: reload so the page totals reflect the change.
        window.location.reload();
        return;
      }

      // In the drawer: update in place using the events the theme already listens to.
      // `cart.sections['mini-cart']` (from the update.js response) lets the drawer
      // re-render without a second request.
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cart }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cart }
      }));
    }

    showMessage(text, type) {
      if (!this.message || !text) return;
      this.message.textContent = text;
      this.message.classList.remove('cart-discount__message--error');
      this.message.classList.add('cart-discount__message--' + type);
      this.message.hidden = false;
    }

    hideMessage() {
      if (this.message) this.message.hidden = true;
    }
  });
})();
